import { ChevronRight } from "lucide-react";
import { useLang } from "../i18n";
import { goTo } from "../settings";
import { useNavigate } from "react-router-dom";
import { FadeUp } from "./Reveal";

// Apple.com ana sayfa ritminde büyük, sakin tanıtım blokları.
// Her blok: tek başlık, tek alt başlık, iki metin bağlantısı, tek görsel.

const V2 = "/assets/marketing/v2";

const Tile = ({ block, lang, navigate, testid, tall = false }) => (
  <FadeUp className="h-full">
    <div
      className={`relative flex h-full flex-col items-center overflow-hidden rounded-[2rem] px-8 pb-10 pt-14 text-center ${
        block.dark ? "bg-[#0b0b0d]" : block.tint ? "bg-operofy-blue/[0.07]" : "bg-white border border-black/10"
      }`}
      data-testid={testid}
    >
      {block.tag && (
        <span className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-operofy-blue">{block.tag}</span>
      )}
      <h3
        className={`mt-3 font-display font-black tracking-tighter ${tall ? "text-4xl sm:text-5xl lg:text-6xl" : "text-3xl sm:text-4xl"} ${block.dark ? "" : "text-black"}`}
        style={block.dark ? { color: "#fff" } : undefined}
      >
        {block.title}
      </h3>
      <p
        className={`mt-3 max-w-md text-sm leading-relaxed sm:text-base ${block.dark ? "" : "text-black/55"}`}
        style={block.dark ? { color: "rgba(255,255,255,.6)" } : undefined}
      >
        {block.desc}
      </p>
      {block.price && (
        <div className="mt-5 flex items-baseline justify-center gap-3" data-testid={`${testid}-price`}>
          {block.price.old && (
            <span
              className="text-base font-bold line-through"
              style={{ color: block.dark ? "rgba(255,255,255,.4)" : "rgba(0,0,0,.35)" }}
            >
              {block.price.old}
            </span>
          )}
          <span className="font-display text-3xl font-black" style={{ color: block.dark ? "#4ade80" : "#0D8D42" }}>
            {block.price.now}
          </span>
          <span
            className="rounded-full px-3 py-1 text-[10px] font-extrabold tracking-wide"
            style={{ backgroundColor: "rgba(226,114,91,.15)", color: "#E2725B" }}
          >
            {lang === "en" ? "LAUNCH SPECIAL" : "LANSMANA ÖZEL"}
          </span>
        </div>
      )}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-6">
        {block.links.map((l) => (
          <button
            key={l.label_tr}
            data-testid={`${testid}-link-${l.href.replace(/[^a-z]/g, "")}`}
            onClick={() => goTo(l.href, navigate)}
            className="inline-flex items-center gap-1 text-sm font-semibold text-operofy-blue transition-colors hover:text-operofy-bluehover"
          >
            {lang === "en" ? l.label_en : l.label_tr} <ChevronRight size={15} strokeWidth={2.2} />
          </button>
        ))}
      </div>
      {block.img && (() => {
        // CLS önleme: bilinen görsellerin doğal boyutları
        const d = block.kind === "phone"
          ? { width: 390, height: 844 }
          : block.img.includes("kitapcik-kapak")
            ? { width: 909, height: 1287 }
            : block.img.includes("destek-tile")
              ? { width: 1024, height: 539 }
              : { width: 1920, height: 1080 };
        return (
        <div className="mt-auto flex w-full justify-center pt-10">
          {block.kind === "phone" ? (
            <img src={block.img} alt={block.title} loading="lazy" width={d.width} height={d.height} className={`${tall ? "w-52 sm:w-60" : "w-40 sm:w-48"} rounded-[1.8rem] border-4 border-[#1a1a1c] shadow-2xl`} />
          ) : (
            <img src={block.img} alt={block.title} loading="lazy" width={d.width} height={d.height} className={`${tall ? "max-w-3xl" : "max-w-md"} max-h-80 w-full rounded-2xl border border-black/10 object-cover object-top shadow-2xl`} />
          )}
        </div>
        );
      })()}
    </div>
  </FadeUp>
);

// Lansman kampanyası — ana sayfada hero'nun hemen altında tek başına büyük blok
export const CampaignTile = () => {
  const { lang } = useLang();
  const navigate = useNavigate();

  const big = {
    tag: lang === "en" ? "PARASYO+ · HOME+ INCLUDED" : "PARASYO+ · HOME+ DAHİL",
    title: lang === "en" ? "Your money and your home. One account." : "Paranız ve eviniz. Tek hesapta.",
    desc: lang === "en"
      ? "Everything in ParasyoFree, plus Home+ hosting, higher record quota, cheque tracking, account sharing and priority support."
      : "ParasyoFree'deki her şey, üstüne Home+ ev sahipliği, 550 slot kayıt hakkı, çek-senet, hesap paylaşımı ve öncelikli destek.",
    // Para birimi sayfa diline bağlı: EN → $2.90/mo, TR → 90 TL/ay (17.10.2026 fiyatları)
    price: lang === "en"
      ? { old: null, now: "$2.90/mo" }
      : { old: null, now: "90 TL/ay" },
    links: [
      { label_tr: "Paketi inceleyin", label_en: "See the plan", href: "/paket/parasyo-plus" },
      lang === "en"
        ? { label_tr: "$2,90'a başla", label_en: "Start at $2.90", href: "https://giris.parasyo.com" }
        : { label_tr: "90 TL'ye başla", label_en: "Start at 90 TL", href: "https://giris.parasyo.com" },
    ],
    img: `${V2}/web-bireysel-panel.webp`,
    dark: true,
  };

  return (
    <section className="bg-operofy-light" data-testid="campaign-tile-section">
      <div className="mx-auto max-w-7xl px-6 pt-16 lg:px-8 lg:pt-20">
        <Tile block={big} lang={lang} navigate={navigate} testid="promo-plus" tall />
      </div>
    </section>
  );
};

export const PromoTiles = () => {
  const { lang } = useLang();
  const navigate = useNavigate();

  const tiles = [
    {
      tag: "PARASYOFREE",
      title: lang === "en" ? "Your money, daily." : "Paranızın günlük hâli.",
      desc: lang === "en" ? "Accounts, budgets and receipt scanning. Free for life." : "Hesaplar, bütçe ve fiş okuma. Ömür boyu ücretsiz.",
      links: [{ label_tr: "İnceleyin", label_en: "Explore", href: "/paket/parasyofree" }],
      img: `${V2}/mobil-bireysel-hesaplar.webp`,
      kind: "phone",
      tint: true,
    },
    {
      tag: "PARASYO PRO",
      title: lang === "en" ? "Pro for business." : "İşletmeniz için Pro.",
      desc: lang === "en" ? "Cash, invoices, stock, staff, roles and reports." : "Kasa, fatura, stok, personel, yetkiler ve raporlar.",
      links: [{ label_tr: "Pro'yu inceleyin", label_en: "Explore Pro", href: "/paket/parasyo-pro" }],
      img: `${V2}/web-ozet.webp`,
    },
    {
      tag: "HOME+",
      title: lang === "en" ? "The household OS." : "Evin işletim sistemi.",
      desc: lang === "en" ? "Chores become a game: wheel, points, rewards." : "Ev işleri kavga değil oyun: çark, puan, ödül.",
      links: [{ label_tr: "Home+'ı keşfedin", label_en: "Discover Home+", href: "/home-plus" }],
      img: `${V2}/mobil-homeplus-gorev-carki.webp`,
      kind: "phone",
    },
    {
      tag: lang === "en" ? "ADD-ON" : "EKLENTİ",
      title: lang === "en" ? "Quick Sale (POS)." : "Hızlı Satış (POS).",
      desc: lang === "en" ? "Barcode, open account, Z-report. Inside the same app." : "Barkod, veresiye, Z raporu. Aynı uygulamanın içinde.",
      links: [{ label_tr: "POS'u inceleyin", label_en: "Explore POS", href: "/pos" }],
      img: `${V2}/web-pos.webp`,
      dark: true,
    },
    {
      tag: lang === "en" ? "GUIDE" : "KILAVUZ",
      title: lang === "en" ? "The interactive booklet." : "İnteraktif kitapçık.",
      desc: lang === "en" ? "19 chapters with real app screens. Get to know Parasyo." : "Gerçek ekranlarla 19 bölüm. Parasyo'yu yakından tanıyın.",
      links: [{ label_tr: "Okumaya başlayın", label_en: "Start reading", href: "/kitapcik" }],
      img: "/assets/kitapcik/kitapcik-kapak.png",
      tint: true,
    },
    {
      tag: lang === "en" ? "SUPPORT" : "DESTEK",
      title: lang === "en" ? "We're here." : "Yanınızdayız.",
      desc: lang === "en" ? "Guides, FAQ and live support — one place." : "Rehberler, SSS ve canlı destek — tek yerde.",
      links: [{ label_tr: "Destek merkezi", label_en: "Support center", href: "/destek" }],
      img: "/assets/kitapcik/destek-tile.png",
    },
  ];

  return (
    <section className="border-t border-black/10 bg-operofy-light" data-testid="promo-tiles">
      <div className="mx-auto max-w-7xl space-y-6 px-6 py-16 lg:px-8 lg:py-20">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {tiles.slice(0, 2).map((b, i) => <Tile key={i} block={b} lang={lang} navigate={navigate} testid={`promo-tile-${i}`} />)}
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {tiles.slice(2, 4).map((b, i) => <Tile key={i + 2} block={b} lang={lang} navigate={navigate} testid={`promo-tile-${i + 2}`} />)}
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {tiles.slice(4).map((b, i) => <Tile key={i + 4} block={b} lang={lang} navigate={navigate} testid={`promo-tile-${i + 4}`} />)}
        </div>
      </div>
    </section>
  );
};
